import React, { useMemo, useState } from 'react';
import { Plus, PencilSimple, Trash, CircleNotch } from '@phosphor-icons/react';
import {
    getObligationStats,
    getNextUnpaidPayment,
    getTotalUnpaidAmount,
    formatEndDate,
    fmtPLN,
} from './obligationUtils';
import MarkMonthPaidButton from './MarkMonthPaidButton';
import NadplataPanel from './NadplataPanel';
import CreditViewSwitch from './CreditViewSwitch';

export default function CorporateCreditsTab({
    corporateCredits,
    loading,
    onAddCredit,
    onEditCredit,
    onDeleteCredit,
    onRefresh,
}) {
    const summary = useMemo(() => {
        let nextSum = 0;
        let totalUnpaid = 0;
        let unpaidCount = 0;
        for (const c of corporateCredits) {
            const next = getNextUnpaidPayment(c.payments);
            if (next) nextSum += next.amount;
            totalUnpaid += getTotalUnpaidAmount(c.payments);
            unpaidCount += (Array.isArray(c.payments) ? c.payments.filter(p => !p?.paid).length : 0);
        }
        return { nextSum, totalUnpaid, unpaidCount, count: corporateCredits.length };
    }, [corporateCredits]);
    const [view, setView] = useState('lista');

    return (
        <div>
            <CreditViewSwitch view={view} setView={setView} kind="corporate" />
            {view === 'nadplata' ? <NadplataPanel kind="corporate" credits={corporateCredits} onDone={onRefresh} /> : (<>
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-bold">Kredyty firmowe ({corporateCredits.length})</h2>
                <button
                    onClick={() => onAddCredit('corporate')}
                    className="btn-primary flex items-center gap-2"
                    data-testid="add-corporate-credit-btn"
                >
                    <Plus className="w-4 h-4" /> Dodaj kredyt
                </button>
            </div>
            <div className="mb-4">
                <MarkMonthPaidButton
                    scope="corporate"
                    scopeLabel="kredytach firmowych"
                    onDone={onRefresh}
                    testid="mark-paid-corporate"
                />
            </div>
            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <CircleNotch className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
            ) : corporateCredits.length === 0 ? (
                <div className="text-center py-20 text-muted-foreground">
                    Brak kredytów. Dodaj pierwszy!
                </div>
            ) : (
                <div className="bg-white border border-border rounded-sm overflow-x-auto -mx-3 sm:mx-0">
                    <table className="w-full min-w-[900px]">
                        <thead className="bg-muted">
                            <tr>
                                <th className="text-left py-3 px-4 field-label">Nazwa</th>
                                <th className="text-right py-3 px-4 field-label">Raty (opł. / razem)</th>
                                <th className="text-right py-3 px-4 field-label">Pozostało</th>
                                <th className="text-right py-3 px-4 field-label">Kwota najbliższej raty</th>
                                <th className="text-right py-3 px-4 field-label">Data zakończenia</th>
                                <th className="text-right py-3 px-4 field-label">Akcje</th>
                            </tr>
                        </thead>
                        <tbody>
                            {corporateCredits.map(credit => {
                                const { paid, total, remaining, endDate } = getObligationStats(credit.payments);
                                const next = getNextUnpaidPayment(credit.payments);
                                return (
                                    <tr key={credit.id} className="table-row" data-testid={`corporate-credit-row-${credit.id}`}>
                                        <td className="py-3 px-4 font-medium">{credit.name}</td>
                                        <td className="py-3 px-4 text-right tabular-nums">{paid} / {total}</td>
                                        <td className="py-3 px-4 text-right">
                                            <span
                                                className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${remaining === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}
                                                data-testid={`corporate-credit-remaining-${credit.id}`}
                                            >
                                                {remaining} {remaining === 1 ? 'rata' : 'rat'}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-right tabular-nums font-mono" data-testid={`corporate-credit-next-${credit.id}`}>
                                            {next ? (
                                                <div>
                                                    <div className="font-semibold text-slate-800">{fmtPLN(next.amount)}</div>
                                                    {next.date && (
                                                        <div className="text-[10px] text-slate-500">
                                                            {next.date} · #{next.payment_number}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-emerald-600 text-xs font-semibold">— spłacone</span>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-right text-sm text-slate-700 tabular-nums" data-testid={`corporate-credit-enddate-${credit.id}`}>
                                            {formatEndDate(endDate)}
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <button onClick={() => onEditCredit(credit, 'corporate')} className="p-2 hover:bg-muted rounded-sm">
                                                <PencilSimple className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => onDeleteCredit(credit.id, 'corporate')} className="p-2 hover:bg-red-50 text-red-600 rounded-sm">
                                                <Trash className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot className="bg-emerald-50 border-t-2 border-emerald-300">
                            <tr>
                                <td colSpan={3} className="py-3 px-4 text-right text-xs font-bold text-emerald-800 uppercase tracking-wider">
                                    Podsumowanie ({summary.count} {summary.count === 1 ? 'kredyt' : 'kredytów'}):
                                </td>
                                <td className="py-3 px-4 text-right">
                                    <div className="text-xs text-slate-600">Najbliższe raty razem:</div>
                                    <div className="text-base font-bold tabular-nums font-mono text-emerald-700" data-testid="corporate-credits-next-sum">
                                        {fmtPLN(summary.nextSum)}
                                    </div>
                                </td>
                                <td colSpan={2} className="py-3 px-4 text-right">
                                    <div className="text-xs text-slate-600">Wszystkie nieopłacone razem ({summary.unpaidCount}):</div>
                                    <div className="text-base font-bold tabular-nums font-mono text-slate-800" data-testid="corporate-credits-total-unpaid">
                                        {fmtPLN(summary.totalUnpaid)}
                                    </div>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
            </>)}
        </div>
    );
}
